/*
  © 2024–2026 Atlas PHM
  Independently developed software. All rights reserved.
*/

/**
 * Compact Print View — 4-Page Decision Document
 *
 * Ultra-compact print format with QR-linked animated story.
 *
 * Design philosophy:
 * - Print helps customer DECIDE (not understand everything)
 * - Depth stays optional (behind QR)
 * - Hard limit: 4 pages
 *
 * Page structure:
 * 1. Context & Intent (your home)
 * 2. Two Flagged Options (A vs B)
 * 3. Cost Comparison
 * 4. Next Steps + QR
 */

import type { CompactPrintData, FlaggedOption } from '@core/compactPrint';
import { BEHAVIORAL_ARCHETYPES } from '@core/presentationPrint';
import { QRCode } from './QRCode';
import { IP_NOTICE_MINIMAL } from '../../legal/ipNotice';

interface CompactPrintViewProps {
  data: CompactPrintData;
}

// ─── Page 1: Context & Intent ────────────────────────────────────

function ContextPage({ data }: { data: CompactPrintData['context'] }) {
  return (
    <div className="dp-page dp-compact-page dp-context-page">
      <div className="dp-compact-header">
        <h1>{data.title}</h1>
        <p className="dp-subtitle">We understand your home</p>
      </div>

      {/* Indicative guidance notice */}
      <div className="dp-indicative-notice" style={{
        padding: '10px 14px',
        marginBottom: '16px',
        background: '#f0f9ff',
        borderLeft: '3px solid #2563eb',
        borderRadius: '0 6px 6px 0',
        fontSize: '12px',
        color: '#1e40af',
        lineHeight: 1.4
      }}>
        This report provides indicative guidance. Final recommendations follow an in-home survey.
      </div>

      <div className="dp-compact-section">
        <h2>Your Property</h2>
        <p className="dp-property-summary">{data.propertySummary}</p>
        <p className="dp-usage-summary">{data.usageSummary}</p>
      </div>

      <div className="dp-compact-section">
        <h2>What Matters to You</h2>
        <ul className="dp-what-matters">
          {data.whatMatters.map((item, idx) => (
            <li key={idx}>{item}</li>
          ))}
        </ul>
      </div>

      <div className="dp-credibility">
        <p>{data.credibilityStatement}</p>
      </div>
    </div>
  );
}

// ─── Page 2: Two Flagged Options ─────────────────────────────────

function FlaggedOptionCard({ option, label }: { option: FlaggedOption; label: string }) {
  const archetype = BEHAVIORAL_ARCHETYPES[option.archetype];
  const isHeatPump = option.archetype === 'STEADY_CONTINUOUS';

  return (
    <div className="dp-option-card">
      <div className="dp-option-badge">
        {option.designation === 'PRIMARY_RECOMMENDATION' ? '✅' : '⚖️'} {option.badge}
      </div>

      <h3 className="dp-option-name">{option.systemName}</h3>

      <div className="dp-option-archetype">
        <span className="dp-archetype-icon">{archetype.icon}</span>
        <span className="dp-archetype-summary">{option.comfortStyle}</span>
      </div>

      {option.typicalAnnualCost !== null && (
        <div className="dp-option-cost">
          <span className="dp-cost-label">Typical annual cost:</span>
          <span className="dp-cost-value">£{Math.round(option.typicalAnnualCost).toLocaleString()}/year</span>
        </div>
      )}

      <div className="dp-option-fit">
        <p className="dp-why-fits">{option.whyThisFits}</p>
      </div>

      <div className="dp-option-caveat">
        <p><strong>Note:</strong> {option.caveat}</p>
      </div>

      {/* Explicit radiator upgrade note for heat pumps */}
      {isHeatPump && (
        <div className="dp-option-radiator-note">
          <p>
            <strong>Radiator consideration:</strong> May require radiator upgrades
            (typically £3,000–£8,000) if existing emitters are undersized for
            low-temperature operation.
          </p>
        </div>
      )}
    </div>
  );
}

function OptionsPage({ data }: { data: CompactPrintData['options'] }) {
  return (
    <div className="dp-page dp-compact-page dp-options-page">
      <div className="dp-compact-header">
        <h1>{data.title}</h1>
        <p className="dp-subtitle">Here are your two sensible paths</p>
      </div>

      <div className="dp-options-grid">
        <FlaggedOptionCard option={data.primary} label="Option A" />
        <FlaggedOptionCard option={data.secondary} label="Option B" />
      </div>
    </div>
  );
}

// ─── Page 3: Cost Comparison ─────────────────────────────────────

function ComparisonPage({ data }: { data: CompactPrintData['comparison'] }) {
  const formatCost = (cost: number | null) =>
    cost !== null ? `£${Math.round(cost).toLocaleString()}` : '—';

  const formatEnergy = (kwh: number | null) =>
    kwh !== null ? `${Math.round(kwh).toLocaleString()} kWh` : '—';

  return (
    <div className="dp-page dp-compact-page dp-comparison-page">
      <div className="dp-compact-header">
        <h1>{data.title}</h1>
        <p className="dp-subtitle">
          The total heat delivered to your home stays the same. Energy input differs
          due to system efficiency — more efficient systems need less fuel.
        </p>
      </div>

      <table className="dp-comparison-table">
        <thead>
          <tr>
            <th></th>
            <th>Fuel/Electricity Input</th>
            <th>Annual Cost</th>
          </tr>
        </thead>
        <tbody>
          <tr className="dp-row-current">
            <td>{data.current.label}</td>
            <td>{formatEnergy(data.current.energyKwh)}</td>
            <td className="dp-cost-cell">{formatCost(data.current.annualCostGbp)}</td>
          </tr>
          <tr className="dp-row-option-a">
            <td><strong>{data.optionA.label}</strong></td>
            <td>{formatEnergy(data.optionA.energyKwh)}</td>
            <td className="dp-cost-cell dp-highlight">{formatCost(data.optionA.annualCostGbp)}</td>
          </tr>
          <tr className="dp-row-option-b">
            <td><strong>{data.optionB.label}</strong></td>
            <td>{formatEnergy(data.optionB.energyKwh)}</td>
            <td className="dp-cost-cell dp-highlight">{formatCost(data.optionB.annualCostGbp)}</td>
          </tr>
        </tbody>
      </table>

      <p className="dp-comparison-footnote">{data.footnote}</p>
    </div>
  );
}

// ─── Page 4: Next Steps + QR ─────────────────────────────────────

function NextStepsPage({ data }: { data: CompactPrintData['nextSteps'] }) {
  return (
    <div className="dp-page dp-compact-page dp-nextsteps-page">
      <div className="dp-compact-header">
        <h1>{data.title}</h1>
      </div>

      <div className="dp-next-steps-list">
        {data.steps.map((step) => (
          <div key={step.number} className="dp-next-step">
            <span className="dp-step-number">{step.number}</span>
            <span className="dp-step-action">{step.action}</span>
          </div>
        ))}
      </div>

      <div className="dp-qr-section">
        <div className="dp-qr-code">
          <QRCode url={data.qrCode.fullUrl} size={data.qrCode.sizeHint} />
        </div>
        <p className="dp-qr-prompt">{data.qrPrompt}</p>
        <a 
          href={data.qrCode.fullUrl} 
          target="_blank" 
          rel="noreferrer"
          className="dp-qr-url"
          style={{
            color: '#2563eb',
            textDecoration: 'underline',
            fontSize: '14px',
            wordBreak: 'break-all',
            display: 'block',
            marginTop: '8px',
            cursor: 'pointer',
            zIndex: 10,
            position: 'relative'
          }}
        >
          {data.qrCode.fullUrl}
        </a>
      </div>
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────

export function CompactPrintView({ data }: CompactPrintViewProps) {
  return (
    <div className="dp-compact-print">
      <ContextPage data={data.context} />
      <OptionsPage data={data.options} />
      <ComparisonPage data={data.comparison} />
      <NextStepsPage data={data.nextSteps} />

      {/* Footer: Simple copyright + document ID (print only) */}
      <div className="dp-print-footer">
        <p>
          {IP_NOTICE_MINIMAL} |
          Document ID: {data.documentId.toUpperCase()}
        </p>
      </div>
    </div>
  );
}

export default CompactPrintView;
